import type { LessonAiDraft } from "../auth/api-client";

export type LessonAiAdvancedPromptContext = {
  pendingDraft: LessonAiDraft;
  rationale: string;
  sourcePrompt: string;
  kind: "generate" | "refine";
  seed?: LessonAiDraft;
};

export function draftSuggestsAdvancedMode(draft: LessonAiDraft): boolean {
  return (draft.workspaceMode ?? "simple") === "advanced";
}

export function advancedModeRationaleForDraft(draft: LessonAiDraft): string {
  const fromAi = draft.advancedModeRationale?.trim();
  if (fromAi) return fromAi;
  const extra = (draft.starterFiles ?? []).filter((f) => f.path !== "main.py").map((f) => f.path);
  if (extra.length > 0) {
    return `This activity splits code across ${extra.join(", ")} and main.py. Advanced mode lets students use a file browser and practice imports between their own modules, closer to how real Python projects are organized.`;
  }
  return "This activity may work better as a small multi-file project. Advanced mode gives students a file browser to edit helper modules alongside main.py.";
}

export function stripAdvancedModeMetadata(draft: LessonAiDraft): LessonAiDraft {
  const { advancedModeRationale: _rationale, ...rest } = draft;
  return rest;
}

export function draftForSimpleModeRegenerate(draft: LessonAiDraft): LessonAiDraft {
  return stripAdvancedModeMetadata({
    ...draft,
    workspaceMode: "simple",
    starterFiles: undefined,
  });
}

export function buildAdvancedPromptContext(
  draft: LessonAiDraft,
  meta: { sourcePrompt: string; kind: "generate" | "refine"; seed?: LessonAiDraft }
): LessonAiAdvancedPromptContext | null {
  if (!draftSuggestsAdvancedMode(draft)) return null;
  return {
    pendingDraft: draft,
    rationale: advancedModeRationaleForDraft(draft),
    sourcePrompt: meta.sourcePrompt,
    kind: meta.kind,
    seed: meta.seed,
  };
}
